/**
 * Homepage ad video popup (sections/ad-video-popup.liquid).
 *
 * Plays the section's Shopify-hosted video once, muted, in a modal <dialog> over the
 * homepage. The shopper sees the time left, can turn sound on or close it early, and the
 * popup closes itself when the video ends.
 *
 * Nothing is fetched until it has decided to show: the poster and <video> live in a
 * <template> and are only cloned in for a visitor who will actually see the popup.
 *
 * Measurement: sends ad_popup_view, ad_popup_sound_on, ad_popup_close and ad_popup_holdout
 * to GA4, and labels each visitor with the user property ad_popup_group (shown | holdout),
 * so any GA4 report can compare visitors who saw the popup with a random holdout who did
 * not. The holdout share is a theme editor setting.
 *
 * Works in both browser (<ad-video-popup>, window.AdVideoPopup) and Node.js
 * (module.exports), so the decisions are tested without a real player.
 */
(function (exports) {
  'use strict';

  const DEFAULTS = {
    // Poster not loaded by then: skip this time rather than pop a box over a page the
    // shopper has already started using. Not marked as seen, so it can try again.
    readyWaitMs: 4000,
    // Playback expected but the clock has not moved for this long, on a visible tab: the
    // network has stalled. Close rather than leave a frozen countdown on screen.
    stallMs: 8000,
    // Autoplay refused and nobody tapped play: get out of the way.
    blockedIdleMs: 10000,
    // Matches the opacity transition on .ad-popup in dawn--ad-video-popup.css.
    fadeMs: 250,
    // GA4 comes from the Google & YouTube app's pixel, which defines window.gtag some time
    // after this script runs. Hold events this long for it before dropping them.
    gtagWaitMs: 10000,
  };

  const ACTIVE = ['starting', 'playing'];
  const GTAG_POLL_MS = 250;

  function formatRemaining(seconds) {
    const whole = Math.max(0, Math.ceil(Number(seconds) || 0));
    return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, '0')}`;
  }

  // Shopify transcodes an uploaded video into several MP4 renditions. Take the smallest one
  // that is sharp enough for the frame it will fill, capped at 2x density: a 3x phone gains
  // nothing visible from 1080p in a popup and pays for it in megabytes.
  function pickSource(sources, cssWidth, pixelRatio) {
    const usable = (sources || [])
      .filter((source) => source && source.url && source.width > 0)
      .sort((a, b) => a.width - b.width);
    if (!usable.length) return null;

    const target = cssWidth * Math.min(Math.max(Number(pixelRatio) || 1, 1), 2);
    return usable.find((source) => source.width >= target) || usable[usable.length - 1];
  }

  function storageFor(win, frequency) {
    try {
      return frequency === 'visitor' ? win.localStorage : win.sessionStorage;
    } catch (error) {
      return null;
    }
  }

  // Storage that cannot be read counts as not seen. The popup then shows on every homepage
  // load for that browser, which beats never showing it to anyone with storage blocked.
  function hasSeen(win, frequency, key) {
    if (frequency === 'always') return false;
    const store = storageFor(win, frequency);
    try {
      return Boolean(store) && store.getItem(key) !== null;
    } catch (error) {
      return false;
    }
  }

  function markSeen(win, frequency, key) {
    if (frequency === 'always') return;
    const store = storageFor(win, frequency);
    try {
      if (store) store.setItem(key, String(Date.now()));
    } catch (error) {
      // Quota or private mode. Nothing to do.
    }
  }

  function prefersReducedMotion(win) {
    return Boolean(win.matchMedia && win.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  // Each visitor draws a bucket from 0 to 99 once per video and keeps it, so the same person
  // lands in the same group on every visit, and moving the holdout between Off, 20% and 50%
  // re-splits visitors by bucket instead of reshuffling them.
  function assignGroup(win, key, holdoutPercent, random) {
    let bucket;
    try {
      const store = win.localStorage;
      const saved = store.getItem(key);
      bucket = /^\d{1,2}$/.test(saved || '') ? Number(saved) : null;
      if (bucket === null) {
        bucket = Math.floor((random || Math.random)() * 100);
        store.setItem(key, String(bucket));
      }
    } catch (error) {
      // No stable identity to test against. Show the popup and leave the visitor out of the
      // comparison, rather than let them flip between groups from one visit to the next.
      return { name: 'shown', sticky: false };
    }
    return { name: bucket < holdoutPercent ? 'holdout' : 'shown', sticky: true };
  }

  // Queues GA4 calls until window.gtag exists, then sends them in order.
  function createTracker(win, waitMs) {
    const queue = [];
    let timer = null;
    let waited = 0;

    const flush = () => {
      if (typeof win.gtag !== 'function') return false;
      while (queue.length) {
        try {
          win.gtag.apply(win, queue.shift());
        } catch (error) {
          // A broken analytics call must never break the popup.
        }
      }
      return true;
    };

    const send = (...args) => {
      queue.push(args);
      if (flush() || timer) return;
      waited = 0;
      timer = setInterval(() => {
        waited += GTAG_POLL_MS;
        if (flush() || waited >= waitMs) {
          clearInterval(timer);
          timer = null;
          queue.length = 0;
        }
      }, GTAG_POLL_MS);
    };

    return {
      event: (name, params) => send('event', name, params || {}),
      setGroup: (group) => send('set', 'user_properties', { ad_popup_group: group }),
    };
  }

  function readSources(root) {
    const node = root.querySelector('script[data-ad-popup-sources]');
    if (!node) return [];
    try {
      const parsed = JSON.parse(node.textContent);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      return [];
    }
  }

  class AdPopup {
    constructor(root, win, options) {
      this.root = root;
      this.win = win;
      this.doc = root.ownerDocument;
      this.options = Object.assign({}, DEFAULTS, options);

      this.dialog = root.querySelector('dialog');
      this.frame = root.querySelector('[data-ad-popup-frame]');
      this.template = root.querySelector('template[data-ad-popup-media]');
      this.countdown = root.querySelector('[data-ad-popup-countdown]');
      this.soundButton = root.querySelector('[data-ad-popup-sound]');
      this.soundLabel = root.querySelector('[data-ad-popup-sound-label]');
      this.closeButton = root.querySelector('[data-ad-popup-close]');
      this.playButton = root.querySelector('[data-ad-popup-play]');

      this.sectionId = root.dataset.sectionId || '';
      this.videoId = root.dataset.videoId || '';
      this.frequency = root.dataset.frequency || 'session';
      this.storageKey = root.dataset.storageKey || 'inu-ad-popup';
      this.holdoutPercent = Math.min(100, Math.max(0, parseInt(root.dataset.holdout, 10) || 0));
      this.ratio = parseFloat(root.dataset.ratio) || 16 / 9;
      // The Liquid duration fills the badge until the file's own metadata arrives.
      this.fallbackDuration = (parseFloat(root.dataset.durationMs) || 0) / 1000;
      this.sources = readSources(root);
      this.track = createTracker(win, this.options.gtagWaitMs);

      this.state = 'idle'; // idle | loading | starting | playing | blocked | closing
      this.timers = {};
      this.listeners = [];
      this.video = null;
      this.poster = null;
      this.preview = false;
      this.pendingShow = null;
      // A shopper view in progress: reported once when it opens and once when it closes.
      this.viewed = false;
      this.maxTime = 0;
      this.soundTurnedOn = false;
    }

    on(target, type, handler) {
      if (!target) return;
      target.addEventListener(type, handler);
      this.listeners.push([target, type, handler]);
    }

    connect() {
      if (!this.dialog || !this.frame || !this.template) return;
      if (typeof this.dialog.showModal !== 'function') return;

      this.on(this.soundButton, 'click', () => this.toggleSound());
      this.on(this.closeButton, 'click', () => this.close('close_button'));
      this.on(this.playButton, 'click', () => this.resume());
      // The dialog fills the viewport, so a click that lands on the dialog itself and not on
      // anything inside it is a click on the dimmed area around the video.
      this.on(this.dialog, 'click', (event) => {
        if (event.target === this.dialog) this.close('backdrop');
      });
      // Escape closes a modal dialog natively. Run the same cleanup when it does.
      this.on(this.dialog, 'close', () => this.close('escape'));
      this.on(this.doc, 'visibilitychange', () => this.onVisibilityChange());
      // Leaving mid-play must not come back from the back/forward cache with the overlay up.
      this.on(this.win, 'pagehide', () => this.teardown('left_page'));

      if (this.isDesignMode()) {
        this.connectEditor();
        return;
      }

      // Reduced motion skips the popup entirely. A still that closes itself is still an
      // interruption, it says little without the film, and the homepage is complete without
      // it. Those visitors stay out of the comparison too: neither group would show it them.
      if (prefersReducedMotion(this.win)) return;

      const group = assignGroup(this.win, `${this.storageKey}:bucket`, this.holdoutPercent);
      if (group.sticky) this.track.setGroup(group.name);

      if (hasSeen(this.win, this.frequency, this.storageKey)) return;

      if (group.name === 'holdout') {
        markSeen(this.win, this.frequency, this.storageKey);
        this.track.event('ad_popup_holdout', { video_id: this.videoId });
        return;
      }

      this.show();
    }

    disconnect() {
      this.teardown('removed');
      this.listeners.forEach(([target, type, handler]) => target.removeEventListener(type, handler));
      this.listeners = [];
    }

    isDesignMode() {
      return Boolean(this.win.Shopify && this.win.Shopify.designMode);
    }

    // In the theme editor the popup never opens on its own, which would get in the way of
    // editing every other section. Selecting this section previews it instead, ignoring
    // frequency, holdout and reduced motion so the merchant can always see what shoppers get.
    // Previews are never reported to GA4.
    connectEditor() {
      const owns = (event) => Boolean(event.detail) && String(event.detail.sectionId) === this.sectionId;

      this.on(this.doc, 'shopify:section:select', (event) => {
        if (!owns(event)) return;
        this.win.adVideoPopupPreviewing = this.sectionId;
        this.show({ preview: true });
      });
      this.on(this.doc, 'shopify:section:deselect', (event) => {
        if (!owns(event)) return;
        this.win.adVideoPopupPreviewing = null;
        this.close('deselected');
      });

      // Changing a setting re-renders the section while it stays selected. Carry the
      // preview over so a newly picked video plays straight away.
      if (this.win.adVideoPopupPreviewing === this.sectionId) this.show({ preview: true });
    }

    show(options) {
      if (this.state !== 'idle') return;
      this.preview = Boolean(options && options.preview);

      // Opened in a background tab: wait until the shopper actually looks at it.
      if (this.doc.visibilityState === 'hidden') {
        this.pendingShow = options || {};
        return;
      }

      const source = pickSource(this.sources, this.frameWidth(), this.win.devicePixelRatio);
      if (!source) return;

      this.state = 'loading';
      this.maxTime = 0;
      this.soundTurnedOn = false;
      this.frame.insertBefore(this.template.content.cloneNode(true), this.frame.firstChild);
      this.poster = this.frame.querySelector('.ad-popup__poster');
      this.video = this.frame.querySelector('video');
      this.prepareVideo(source.url);
      this.renderCountdown();
      this.renderSound();
      this.waitForPoster();
    }

    // Mirrors .ad-popup__stage in dawn--ad-video-popup.css: 16px gutters, a 1200px cap, and
    // the height left over after the control bar.
    frameWidth() {
      const width = this.win.innerWidth || 0;
      const height = this.win.innerHeight || 0;
      return Math.max(0, Math.min(width - 32, 1200, (height - 84) * this.ratio));
    }

    prepareVideo(url) {
      const video = this.video;
      // Set the properties as well as the attributes: a cloned element's muted attribute
      // does not reliably make it muted, and an unmuted play() without a tap is refused.
      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;
      video.loop = false;
      video.controls = false;

      video.addEventListener('loadedmetadata', () => this.renderCountdown());
      video.addEventListener('durationchange', () => this.renderCountdown());
      video.addEventListener('timeupdate', () => this.onProgress());
      video.addEventListener('playing', () => this.onPlaying());
      video.addEventListener('volumechange', () => this.renderSound());
      video.addEventListener('ended', () => this.close('ended'));
      video.addEventListener('error', () => this.close('error'));

      video.src = url;
    }

    // Open on the poster, never on an empty frame. iOS Safari does not buffer video before
    // play() is called, so the poster (Shopify always generates one) is the signal to wait on.
    waitForPoster() {
      const ready = () => {
        if (this.state !== 'loading') return;
        clearTimeout(this.timers.ready);
        this.open();
      };

      if (!this.poster || (this.poster.complete && this.poster.naturalWidth > 0)) {
        ready();
        return;
      }

      // A broken poster is not worth skipping the film for; the video covers it once it plays.
      this.poster.addEventListener('load', ready, { once: true });
      this.poster.addEventListener('error', ready, { once: true });
      this.timers.ready = setTimeout(() => {
        if (this.state === 'loading') this.close('slow');
      }, this.options.readyWaitMs);
    }

    open() {
      this.state = 'starting';

      const body = this.doc.body;
      this.lockedScroll = !body.classList.contains('overflow-hidden');
      body.classList.add('overflow-hidden');

      this.dialog.classList.remove('is-closing');
      this.dialog.showModal();
      // The dialog itself takes focus rather than its first button, so nothing looks
      // selected before the shopper has done anything. Tab still reaches every control.
      try {
        this.dialog.focus({ preventScroll: true });
      } catch (error) {
        this.dialog.focus();
      }

      if (!this.preview) {
        // Marked when it opens, not when it ends: reloading mid-play must not start it again.
        markSeen(this.win, this.frequency, this.storageKey);
        this.viewed = true;
        this.track.event('ad_popup_view', { video_id: this.videoId });
      }

      this.startWatchdog();
      this.play();
    }

    play() {
      let attempt;
      try {
        attempt = this.video.play();
      } catch (error) {
        attempt = Promise.reject(error);
      }
      if (!attempt || typeof attempt.then !== 'function') return;

      attempt.catch((error) => {
        if (!ACTIVE.includes(this.state)) return;
        const name = error && error.name;
        // Pausing before play() settles (a hidden tab) rejects with AbortError. Not a failure.
        if (name === 'AbortError') return;
        // NotAllowedError: the browser refused even muted autoplay (iOS Low Power Mode, data
        // saver, a per-site setting). Offer a play button instead of a dead frame.
        if (name === 'NotAllowedError') this.block();
        else this.close('error');
      });
    }

    block() {
      this.state = 'blocked';
      clearInterval(this.timers.watchdog);
      if (this.playButton) this.playButton.hidden = false;
      this.timers.idle = setTimeout(() => this.close('autoplay_blocked'), this.options.blockedIdleMs);
    }

    resume() {
      if (this.state !== 'blocked') return;
      clearTimeout(this.timers.idle);
      if (this.playButton) this.playButton.hidden = true;
      this.state = 'starting';
      this.startWatchdog();
      this.play();
    }

    startWatchdog() {
      clearInterval(this.timers.watchdog);
      this.lastProgressAt = Date.now();
      this.lastTime = undefined;
      const every = Math.max(10, Math.min(1000, this.options.stallMs / 2));
      this.timers.watchdog = setInterval(() => this.checkStall(), every);
    }

    checkStall() {
      if (!ACTIVE.includes(this.state)) return;
      if (this.doc.visibilityState === 'hidden') {
        this.lastProgressAt = Date.now();
        return;
      }
      if (Date.now() - this.lastProgressAt > this.options.stallMs) this.close('stalled');
    }

    onPlaying() {
      if (!ACTIVE.includes(this.state)) return;
      this.state = 'playing';
      this.lastProgressAt = Date.now();

      // Keep the poster up until a real frame has been painted, so the swap is invisible.
      const reveal = () => this.frame.classList.add('is-playing');
      if (typeof this.video.requestVideoFrameCallback === 'function') {
        this.video.requestVideoFrameCallback(reveal);
      } else {
        reveal();
      }
    }

    onProgress() {
      if (!this.video) return;
      const time = this.video.currentTime;
      if (time !== this.lastTime) {
        this.lastTime = time;
        this.lastProgressAt = Date.now();
      }
      if (time > this.maxTime) this.maxTime = time;
      this.renderCountdown();
    }

    onVisibilityChange() {
      const hidden = this.doc.visibilityState === 'hidden';

      if (!hidden && this.pendingShow) {
        const options = this.pendingShow;
        this.pendingShow = null;
        this.show(options);
        return;
      }

      if (!this.video) return;
      if (hidden && ACTIVE.includes(this.state)) {
        this.video.pause();
        this.pausedForHidden = true;
      } else if (!hidden && this.pausedForHidden && ACTIVE.includes(this.state)) {
        this.pausedForHidden = false;
        this.lastProgressAt = Date.now();
        this.play();
      }
    }

    duration() {
      const own = this.video && this.video.duration;
      return Number.isFinite(own) && own > 0 ? own : this.fallbackDuration;
    }

    percentWatched(reason) {
      if (reason === 'ended') return 100;
      const total = this.duration();
      if (!(total > 0)) return 0;
      return Math.max(0, Math.min(100, Math.round((this.maxTime / total) * 100)));
    }

    renderCountdown() {
      if (!this.countdown) return;
      const total = this.duration();
      this.countdown.hidden = !(total > 0);
      if (!(total > 0)) return;
      const elapsed = (this.video && this.video.currentTime) || 0;
      this.countdown.textContent = formatRemaining(total - elapsed);
    }

    toggleSound() {
      if (!this.video) return;
      this.video.muted = !this.video.muted;
      this.renderSound();

      // Once per view: turning it off and on again is still one shopper who wanted sound.
      if (this.video.muted || this.soundTurnedOn) return;
      this.soundTurnedOn = true;
      if (this.viewed) {
        this.track.event('ad_popup_sound_on', {
          video_id: this.videoId,
          percent_watched: this.percentWatched(),
        });
      }
    }

    renderSound() {
      if (!this.soundButton) return;
      const muted = !this.video || this.video.muted;
      this.soundButton.dataset.muted = String(muted);
      const label = muted ? this.soundButton.dataset.labelUnmute : this.soundButton.dataset.labelMute;
      if (this.soundLabel && label) this.soundLabel.textContent = label;
    }

    // One close per view. A popup that never opened (poster too slow) was never seen, and
    // editor previews are not shopper views, so neither is reported.
    report(reason) {
      if (!this.viewed) return;
      this.viewed = false;
      this.track.event('ad_popup_close', {
        video_id: this.videoId,
        close_reason: reason,
        percent_watched: this.percentWatched(reason),
        sound_on: this.soundTurnedOn ? 'yes' : 'no',
      });
    }

    close(reason) {
      this.pendingShow = null;
      if (this.state === 'idle' || this.state === 'closing') return;
      this.closeReason = reason;
      this.report(reason);
      this.clearTimers();

      // Silence it now. The last frame stays up while the overlay fades.
      if (this.video) {
        this.video.muted = true;
        this.video.pause();
      }

      if (!this.dialog.open) {
        this.finish();
        return;
      }

      this.state = 'closing';
      this.dialog.classList.add('is-closing');
      this.timers.fade = setTimeout(() => this.finish(), this.options.fadeMs);
    }

    // Close straight away with no fade: the page is going, or the section is being removed.
    teardown(reason) {
      this.pendingShow = null;
      if (this.state === 'idle') return;
      if (this.state !== 'closing') this.closeReason = reason;
      this.report(reason);
      this.clearTimers();
      this.finish();
    }

    clearTimers() {
      clearTimeout(this.timers.ready);
      clearTimeout(this.timers.idle);
      clearTimeout(this.timers.fade);
      clearInterval(this.timers.watchdog);
    }

    finish() {
      clearTimeout(this.timers.fade);
      // Idle first, so the 'close' event this fires is recognised as already handled.
      this.state = 'idle';
      if (this.dialog.open) this.dialog.close();
      this.dialog.classList.remove('is-closing');

      if (this.lockedScroll) {
        this.doc.body.classList.remove('overflow-hidden');
        this.lockedScroll = false;
      }

      // Release the file so nothing keeps downloading or decoding, then drop the cloned media
      // so an editor preview starts clean.
      if (this.video) {
        this.video.pause();
        this.video.removeAttribute('src');
        try {
          this.video.load();
        } catch (error) {
          // jsdom and very old browsers. The element is removed next anyway.
        }
      }
      this.frame.querySelectorAll('.ad-popup__poster, .ad-popup__video').forEach((node) => node.remove());
      this.frame.classList.remove('is-playing');
      if (this.playButton) this.playButton.hidden = true;

      this.video = null;
      this.poster = null;
      this.pausedForHidden = false;
    }
  }

  exports.DEFAULTS = DEFAULTS;
  exports.formatRemaining = formatRemaining;
  exports.pickSource = pickSource;
  exports.hasSeen = hasSeen;
  exports.assignGroup = assignGroup;
  exports.createTracker = createTracker;
  exports.AdPopup = AdPopup;

  if (typeof window !== 'undefined') {
    window.AdVideoPopup = exports;

    if (window.customElements && !window.customElements.get('ad-video-popup')) {
      window.customElements.define(
        'ad-video-popup',
        class extends HTMLElement {
          connectedCallback() {
            this.popup = new AdPopup(this, window);
            this.popup.connect();
          }

          disconnectedCallback() {
            if (this.popup) this.popup.disconnect();
            this.popup = null;
          }
        }
      );
    }
  }
})(typeof module !== 'undefined' && module.exports ? module.exports : {});
